import { ComponentType, ReactNode, useMemo, useRef, useState } from 'react';
import Dialog from '#shared/components/Dialog/Dialog';
import { DialogComponentProps, DialogContext, DialogOptions, DialogService } from './DialogContext';

// Shared owns the dialog UI: the content renders here, under every shared context, so a dialog
// component can call useApi()/useAlertDialog()/etc. without any re-wrapping.
export default function DialogProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<DialogOptions>({});
  const [content, setContent] = useState<ReactNode>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Cancels the dialog currently showing, resolving its promise with null (same as onCancel). Used by
  // the Radix close paths (Escape / outside click) and by a re-entrant open() replacing a dialog that
  // is still up — without it that first caller would await forever.
  const cancelRef = useRef<() => void>(() => {});

  function open<ReturnType>(Component: ComponentType<DialogComponentProps<ReturnType>>, opts: DialogOptions = {}) {
    return new Promise<ReturnType | null>((resolve) => {
      const confirm = (value: ReturnType) => {
        setIsOpen(false);
        resolve(value);
      };

      const cancel = () => {
        setIsOpen(false);
        resolve(null);
      };

      cancelRef.current();
      cancelRef.current = cancel;
      setOptions(opts);
      setContent(<Component onConfirm={confirm} onCancel={cancel} />);
      setIsOpen(true);
    });
  }

  // Built once: open() only closes over state setters and cancelRef, all stable, so consumers get one
  // service identity for the provider's lifetime (see AlertDialog.provider).
  const service = useMemo<DialogService>(() => ({ open }), []);

  return (
    <DialogContext.Provider value={service}>
      {children}

      <Dialog isOpen={isOpen} onClose={() => cancelRef.current()}>
        <Dialog.Content {...options}>{content}</Dialog.Content>
      </Dialog>
    </DialogContext.Provider>
  );
}
